import { useState } from 'react'
import { Button } from '#app/components/ui/button'
import { Icon } from '#app/components/ui/icon'
import { InputText } from '#app/components/ui/input-text'

interface LocatarioData {
	locatarioNombre: string
	locatarioDocumento: string
	locatarioDomicilio: string
	locatarioTelefono: string
	locatarioEmail: string
}

interface CaucionAlquilerLocatarioScreenProps {
	onNext: (data: LocatarioData) => void
	onBack: () => void
	data?: Partial<LocatarioData>
}

export function CaucionAlquilerLocatarioScreen({
	onNext,
	onBack,
	data,
}: CaucionAlquilerLocatarioScreenProps) {
	const [formData, setFormData] = useState<LocatarioData>({
		locatarioNombre: data?.locatarioNombre || '',
		locatarioDocumento: data?.locatarioDocumento || '',
		locatarioDomicilio: data?.locatarioDomicilio || '',
		locatarioTelefono: data?.locatarioTelefono || '',
		locatarioEmail: data?.locatarioEmail || '',
	})

	const [errors, setErrors] = useState<Record<string, string>>({})

	const validateEmail = (email: string) => {
		return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
	}

	const handleSubmit = () => {
		const newErrors: Record<string, string> = {}

		if (!formData.locatarioNombre.trim()) {
			newErrors.locatarioNombre = 'Ingresa el nombre del locatario'
		}
		if (!formData.locatarioDocumento.trim()) {
			newErrors.locatarioDocumento = 'Ingresa el CUIT/DNI'
		}
		if (!formData.locatarioDomicilio.trim()) {
			newErrors.locatarioDomicilio = 'Ingresa el domicilio'
		}
		if (!formData.locatarioTelefono.trim()) {
			newErrors.locatarioTelefono = 'Ingresa el teléfono'
		}
		if (!formData.locatarioEmail.trim()) {
			newErrors.locatarioEmail = 'Ingresa el email'
		} else if (!validateEmail(formData.locatarioEmail)) {
			newErrors.locatarioEmail = 'Email inválido'
		}

		if (Object.keys(newErrors).length > 0) {
			setErrors(newErrors)
			return
		}

		onNext(formData)
	}

	const isFormValid =
		formData.locatarioNombre.trim() &&
		formData.locatarioDocumento.trim() &&
		formData.locatarioDomicilio.trim() &&
		formData.locatarioTelefono.trim() &&
		formData.locatarioEmail.trim()

	return (
		<div className="flex h-full w-full flex-col">
			<div className="flex-1 overflow-y-auto">
				<div className="flex w-full flex-col items-center gap-8">
					<h2 className="font-parkinsans text-center text-2xl leading-8 font-medium text-blue-800">
						Datos del Locatario
						<br />
						<span className="text-lg text-neutral-600">(Inquilino)</span>
					</h2>

					<div className="flex w-full flex-col gap-4">
						<InputText
							label="Nombre y Apellido / Razón Social"
							labelPosition="internal"
							size="l"
							placeholder="Nombre del locatario"
							value={formData.locatarioNombre}
							onChange={(e) => {
								setFormData({ ...formData, locatarioNombre: e.target.value })
								setErrors({ ...errors, locatarioNombre: '' })
							}}
							errorText={errors.locatarioNombre}
							required
						/>
						<InputText
							label="CUIT / DNI"
							labelPosition="internal"
							size="l"
							placeholder="Documento del locatario"
							value={formData.locatarioDocumento}
							onChange={(e) => {
								setFormData({ ...formData, locatarioDocumento: e.target.value })
								setErrors({ ...errors, locatarioDocumento: '' })
							}}
							errorText={errors.locatarioDocumento}
							required
						/>
						<InputText
							label="Domicilio Legal"
							labelPosition="internal"
							size="l"
							placeholder="Dirección actual del locatario"
							value={formData.locatarioDomicilio}
							onChange={(e) => {
								setFormData({ ...formData, locatarioDomicilio: e.target.value })
								setErrors({ ...errors, locatarioDomicilio: '' })
							}}
							errorText={errors.locatarioDomicilio}
							required
						/>
						<InputText
							label="Teléfono"
							labelPosition="internal"
							size="l"
							placeholder="Teléfono de contacto"
							value={formData.locatarioTelefono}
							onChange={(e) => {
								setFormData({ ...formData, locatarioTelefono: e.target.value })
								setErrors({ ...errors, locatarioTelefono: '' })
							}}
							errorText={errors.locatarioTelefono}
							required
						/>
						<InputText
							label="Email"
							labelPosition="internal"
							size="l"
							placeholder="correo@ejemplo.com"
							type="email"
							value={formData.locatarioEmail}
							onChange={(e) => {
								setFormData({ ...formData, locatarioEmail: e.target.value })
								setErrors({ ...errors, locatarioEmail: '' })
							}}
							errorText={errors.locatarioEmail}
							required
						/>
					</div>
				</div>
			</div>

			<div className="flex w-full shrink-0 flex-col items-center justify-start gap-2 bg-white pt-4 md:flex-row md:justify-between md:bg-transparent md:pt-8">
				<Button
					variant="primary"
					appearance="ghost"
					size="l"
					onClick={onBack}
					className="order-2 w-full gap-2 rounded-xl px-4 py-3 text-sm leading-4 md:order-1 md:w-auto md:px-6 md:py-4 md:text-base md:leading-5"
				>
					<Icon name="arrow-left" className="size-4" />
					Atrás
				</Button>
				<Button
					variant="secondary"
					appearance="solid"
					size="l"
					onClick={handleSubmit}
					disabled={!isFormValid}
					className="order-1 w-full md:order-2 md:w-auto"
				>
					Siguiente
					<Icon name="arrow-right" className="size-3.5" />
				</Button>
			</div>
		</div>
	)
}
